/**
 * FlowVisualization — React Flow canvas for flow tree rendering.
 *
 * Converts FlowTreeManager state into React Flow nodes and edges.
 * Handles node clicks to expand/collapse the tree.
 * Supports cross-session continuation with distinct styling.
 *
 * Shared across Event Flow, Screen Flow, etc.
 */
import { useMemo, useCallback, useRef, useEffect } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  useReactFlow,
  ReactFlowProvider,
  type Edge,
  type NodeMouseHandler,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { FlowNodeMemo, type FlowNodePayload } from './FlowNode';
import type { FlowLevel } from './flow.types';
import { FlowGraphBuilder, FlowTreeManager, pathToString } from './FlowGraphEngine';
import type { FlowRecord } from './flow.types';
import { COLUMN_WIDTH, Y_START, SESSION_COLORS, NODE_GAP } from './flow.constants';

/**
 * Estimate a node's rendered height based on its content.
 * Must stay in sync with FlowNode's layout (padding, rows, etc.).
 */
function estimateNodeHeight(opts: {
  isTerminal: boolean;
  hasTime: boolean;
  hasSessionKpi: boolean;
  hasCrossKpi: boolean;
}): number {
  let h = 24; // vertical padding
  h += 22;    // event name row
  h += 6;     // gap
  h += 28;    // count + pct row
  if (!opts.isTerminal) {
    h += 6;   // gap
    h += 8;   // percentage bar
  }
  if (opts.hasTime) {
    h += 6;   // gap
    h += 18;  // timing row
  }
  if (opts.hasSessionKpi || opts.hasCrossKpi) {
    h += 6;   // gap
    h += 10;  // separator (pt-1.5 + mt-0.5 + border)
    if (opts.hasSessionKpi) h += 20; // session KPI row
    if (opts.hasSessionKpi && opts.hasCrossKpi) h += 4; // gap between KPI rows
    if (opts.hasCrossKpi) h += 20;   // cross-session KPI row
  }
  return h;
}

/* ─── Node Types ──────────────────────────────────────────────────────────── */

const nodeTypes = { flowNode: FlowNodeMemo };

/* ─── Props ───────────────────────────────────────────────────────────────── */

interface FlowVisualizationProps {
  /** Raw flow records from API */
  records: FlowRecord[];
  /** Current tree levels (state) */
  levels: FlowLevel[];
  /** Callback when levels change (node click) */
  onLevelsChange: (levels: FlowLevel[]) => void;
  /** KPI event name (for percentage display) */
  kpiEvent?: string;
  /** Starting count (first node) — used for absolute percentages */
  startingCount: number | null;
  /** Starting KPI count (first node) — used for absolute KPI percentages */
  startingKpiCount: number | null;
  /** Accent color for selected nodes */
  accentColor: string;
  /** Callback when a (none) node is clicked for continuation */
  onContinuationRequest?: (level: number, sessionIndex: number) => void;
  /** Set of level indices where continuation is currently loading */
  continuationLoadingLevels?: Set<number>;
  /** Continuation records by session index — merged into the graph builder */
  continuationRecordsMap?: Map<number, FlowRecord[]>;
}

/* ─── Inner Component (needs ReactFlowProvider context) ───────────────────── */

function FlowVisualizationInner({
  records,
  levels,
  onLevelsChange,
  kpiEvent,
  startingCount,
  startingKpiCount,
  accentColor,
  onContinuationRequest,
  continuationLoadingLevels,
  continuationRecordsMap,
}: FlowVisualizationProps) {
  const { fitView } = useReactFlow();
  const prevLevelCountRef = useRef(levels.length);

  // Build graph from records + merge any continuation data
  const graphBuilder = useMemo(() => {
    const builder = new FlowGraphBuilder(records);
    if (continuationRecordsMap) {
      for (const [sessionIndex, contRecords] of continuationRecordsMap) {
        builder.mergeContinuation(contRecords, sessionIndex);
      }
    }
    return builder;
  }, [records, continuationRecordsMap]);

  // Fit view when levels change
  useEffect(() => {
    if (levels.length !== prevLevelCountRef.current) {
      prevLevelCountRef.current = levels.length;
      const timer = setTimeout(() => fitView({ padding: 0.15, duration: 300 }), 50);
      return () => clearTimeout(timer);
    }
  }, [levels.length, fitView]);

  // Convert levels to React Flow nodes + edges
  const { nodes, edges } = useMemo(() => {
    const rfNodes: Array<{ id: string; type: string; position: { x: number; y: number }; data: Record<string, unknown> }> = [];
    const rfEdges: Edge[] = [];

    // Pre-compute cumulative time along the selected path.
    // cumulativeAtLevel[i] = total avg time from the first node to the selected node at level i.
    // For (none) nodes that have been continued, inject the none_duration from the graph builder.
    const cumulativeAtLevel: number[] = [];
    const noneStepAtLevel: (number | null)[] = [];
    let runningTime = 0;
    for (let i = 0; i < levels.length; i++) {
      const lev = levels[i];
      let noneStep: number | null = null;
      if (lev.selected === '(none)') {
        // Check if next level is a continuation — if so, use the none_duration
        const nextLevel = i + 1 < levels.length ? levels[i + 1] : null;
        const nextSessionIndex = nextLevel?.sessionIndex ?? 0;
        if (nextSessionIndex > 0) {
          const dur = graphBuilder.noneDurations.get(nextSessionIndex);
          if (dur != null && dur > 0) {
            runningTime += dur;
            noneStep = dur;
          }
        }
      } else if (lev.selected) {
        const selNode = lev.nodes.find(n => n.event === lev.selected);
        if (selNode?.avgTimeSec != null) {
          runningTime += selNode.avgTimeSec;
        }
      }
      cumulativeAtLevel.push(runningTime);
      noneStepAtLevel.push(noneStep);
    }

    // The very first node count (level 0) — used for continuation % relative to global start
    const globalFirstCount = levels.length > 0 && levels[0].nodes.length > 0
      ? Math.max(...levels[0].nodes.filter(n => n.event !== '(none)').map(n => n.count), 0)
      : 0;

    for (let levelIdx = 0; levelIdx < levels.length; levelIdx++) {
      const levelData = levels[levelIdx];
      const x = levelIdx * COLUMN_WIDTH;
      const levelSessionIndex = levelData.sessionIndex ?? 0;

      const isContinuationStartLevel = levelSessionIndex > 0 && (
        levelIdx === 0 || (levels[levelIdx - 1].sessionIndex ?? 0) !== levelSessionIndex
      );

      const isLastLevel = levelIdx === levels.length - 1;

      // First pass: precompute per-node data to estimate heights
      const levelNodeData: Array<{
        node: typeof levelData.nodes[0];
        isTerminal: boolean;
        isSelected: boolean;
        isFirstNode: boolean;
        canContinue: boolean;
        isContinuationLoading: boolean;
        kpiPct: number | null;
        kpiCount: number | null;
        crossSessionKpiPct: number | null;
        crossSessionKpiCount: number | null;
        relativePct: number;
        absolutePct: number;
        effectiveAvgTimeSec: number | null;
        cumulativeTimeSec: number | null;
      }> = [];

      for (let nodeIdx = 0; nodeIdx < levelData.nodes.length; nodeIdx++) {
        const node = levelData.nodes[nodeIdx];

        const isTerminal = node.event === '(none)';
        const isSelected = levelData.selected === node.event;
        const isFirstNode = levelIdx === 0;

        const canContinue = isTerminal && !!onContinuationRequest;
        const isContinuationLoading = isTerminal && (continuationLoadingLevels?.has(levelIdx) ?? false);

        // Build KPI lookup key at a given level — skip breakdown levels, continuation sessions use session prefix
        const buildKpiKeyAt = (evt: string, atLevel: number): string => {
          if (levelSessionIndex > 0) {
            const contPath: string[] = [];
            for (let i = 0; i < atLevel; i++) {
              if (levels[i].sessionIndex === levelSessionIndex && levels[i].selected) {
                contPath.push(levels[i].selected!);
              }
            }
            contPath.push(evt);
            return `__s${levelSessionIndex}__` + pathToString(contPath);
          }
          const p: string[] = [];
          for (let i = 0; i < atLevel; i++) {
            if (levels[i].selected) p.push(levels[i].selected!);
          }
          p.push(evt);
          return pathToString(p);
        };

        let kpiPct: number | null = null;
        let kpiCount: number | null = null;
        let crossSessionKpiPct: number | null = null;
        let crossSessionKpiCount: number | null = null;

        if (kpiEvent && !isTerminal) {
          const kpiLookupKey = buildKpiKeyAt(node.event, levelIdx);

          const rawPct = graphBuilder.kpiPercentages[kpiLookupKey] ?? null;
          if (rawPct != null) {
            kpiCount = Math.round((node.count * rawPct) / 100);
            kpiPct = rawPct;
          }

          const rawCrossPct = graphBuilder.crossSessionKpiPercentages[kpiLookupKey] ?? null;
          if (rawCrossPct != null) {
            crossSessionKpiCount = Math.round((node.count * rawCrossPct) / 100);
            crossSessionKpiPct = rawCrossPct;
          }
        } else if (kpiEvent && isTerminal && levelIdx > 0) {
          const parentLevelIdx = levelIdx - 1;
          const parentSelected = levels[parentLevelIdx].selected;
          if (parentSelected) {
            const parentKey = buildKpiKeyAt(parentSelected, parentLevelIdx);
            const parentCrossPct = graphBuilder.crossSessionKpiPercentages[parentKey] ?? null;
            if (parentCrossPct != null) {
              const parentNode = levels[parentLevelIdx].nodes.find(n => n.event === parentSelected);
              const parentCnt = parentNode?.count ?? 0;
              const parentCrossCount = Math.round(parentCnt * parentCrossPct / 100);

              let siblingCrossTotal = 0;
              for (const sibling of levelData.nodes) {
                if (sibling.event === '(none)') continue;
                const sibKey = buildKpiKeyAt(sibling.event, levelIdx);
                const sibPct = graphBuilder.crossSessionKpiPercentages[sibKey] ?? null;
                if (sibPct != null) {
                  siblingCrossTotal += Math.round(sibling.count * sibPct / 100);
                }
              }

              const derivedCount = Math.max(0, parentCrossCount - siblingCrossTotal);
              if (node.count > 0) {
                crossSessionKpiCount = derivedCount;
                crossSessionKpiPct = (derivedCount / node.count) * 100;
              }
            }
          }
        }

        const relativePct = node.pct;
        let absolutePct = node.pct;
        if (isContinuationStartLevel && globalFirstCount > 0) {
          absolutePct = node.count / globalFirstCount;
        } else if (startingCount && startingCount > 0) {
          absolutePct = node.count / startingCount;
        }

        let effectiveAvgTimeSec = node.avgTimeSec ?? null;
        if (isTerminal && isSelected && noneStepAtLevel[levelIdx] != null) {
          effectiveAvgTimeSec = noneStepAtLevel[levelIdx];
        }

        let cumulativeTimeSec: number | null = null;
        if (levelIdx > 0) {
          const parentCumulative = cumulativeAtLevel[levelIdx - 1] ?? 0;
          const nodeStepTime = effectiveAvgTimeSec ?? 0;
          const total = parentCumulative + nodeStepTime;
          if (total > 0) cumulativeTimeSec = total;
        }

        levelNodeData.push({
          node, isTerminal, isSelected, isFirstNode,
          canContinue, isContinuationLoading,
          kpiPct, kpiCount, crossSessionKpiPct, crossSessionKpiCount,
          relativePct, absolutePct, effectiveAvgTimeSec, cumulativeTimeSec,
        });
      }

      // Compute Y positions using estimated heights + fixed gap
      const yPositions: number[] = [];
      let currentY = Y_START;
      for (let nodeIdx = 0; nodeIdx < levelNodeData.length; nodeIdx++) {
        yPositions.push(currentY);
        const d = levelNodeData[nodeIdx];
        const hasTime = !d.isFirstNode && (d.isTerminal
          ? (d.effectiveAvgTimeSec != null || d.cumulativeTimeSec != null)
          : true) && (d.effectiveAvgTimeSec != null || d.cumulativeTimeSec != null);
        const h = estimateNodeHeight({
          isTerminal: d.isTerminal,
          hasTime,
          hasSessionKpi: d.kpiPct != null,
          hasCrossKpi: d.crossSessionKpiPct != null,
        });
        currentY += h + NODE_GAP;
      }

      // Second pass: create React Flow nodes and edges
      for (let nodeIdx = 0; nodeIdx < levelNodeData.length; nodeIdx++) {
        const d = levelNodeData[nodeIdx];
        const nodeId = `node-${levelIdx}-${nodeIdx}`;

        rfNodes.push({
          id: nodeId,
          type: 'flowNode',
          position: { x, y: yPositions[nodeIdx] },
          data: {
            event: d.node.event,
            count: d.node.count,
            relativePct: d.relativePct,
            absolutePct: d.absolutePct,
            kpiPct: d.kpiPct,
            kpiCount: d.kpiCount,
            crossSessionKpiPct: d.crossSessionKpiPct,
            crossSessionKpiCount: d.crossSessionKpiCount,
            isSelected: d.isSelected,
            isTerminal: d.isTerminal,
            level: levelIdx,
            accentColor,
            startingCount,
            sessionIndex: levelSessionIndex,
            canContinue: d.canContinue,
            isContinuationLoading: d.isContinuationLoading,
            avgTimeSec: d.effectiveAvgTimeSec,
            cumulativeTimeSec: d.cumulativeTimeSec,
            isFirstNode: d.isFirstNode,
            isContinuationStart: isContinuationStartLevel,
          } satisfies FlowNodePayload as unknown as Record<string, unknown>,
        });

        if (levelIdx > 0) {
          const parentLevel = levels[levelIdx - 1];
          const parentSelected = parentLevel.selected;
          if (parentSelected) {
            const parentNodeIdx = parentLevel.nodes.findIndex((n) => n.event === parentSelected);
            if (parentNodeIdx >= 0) {
              const parentId = `node-${levelIdx - 1}-${parentNodeIdx}`;
              const parentSessionIndex = parentLevel.sessionIndex ?? 0;
              const isCrossSession = levelSessionIndex !== parentSessionIndex;
              const sessionColor = SESSION_COLORS[levelSessionIndex % SESSION_COLORS.length];

              rfEdges.push({
                id: `edge-${parentId}-${nodeId}`,
                source: parentId,
                target: nodeId,
                type: 'smoothstep',
                style: {
                  stroke: isCrossSession ? (sessionColor.accent ?? '#94a3b8') : '#94a3b8',
                  strokeWidth: isCrossSession ? 3 : 2,
                  strokeDasharray: isCrossSession ? '8 4' : undefined,
                },
                animated: d.isSelected,
              });
            }
          }
        }
      }
    }

    return { nodes: rfNodes, edges: rfEdges };
  }, [levels, kpiEvent, startingCount, startingKpiCount, accentColor, graphBuilder, onContinuationRequest, continuationLoadingLevels]);

  // Handle node click: expand, collapse, or request continuation
  const onNodeClick: NodeMouseHandler = useCallback(
    (_event, node) => {
      const data = node.data as unknown as FlowNodePayload;
      const clickedLevel = data.level;
      const clickedEvent = data.event;
      const clickedSessionIndex = data.sessionIndex;

      if (clickedLevel >= levels.length) return;

      const treeManager = new FlowTreeManager(graphBuilder);
      treeManager.levels = structuredClone(levels);

      const currentSelection = treeManager.levels[clickedLevel].selected;

      if (clickedEvent === '(none)') {
        if (data.canContinue && onContinuationRequest) {
          onContinuationRequest(clickedLevel, clickedSessionIndex);
        } else {
          treeManager.selectTerminal(clickedLevel, clickedEvent);
          onLevelsChange(treeManager.levels);
        }
      } else if (currentSelection === clickedEvent && clickedLevel + 1 < treeManager.levels.length) {
        treeManager.levels = treeManager.levels.slice(0, clickedLevel + 1);
        onLevelsChange(treeManager.levels);
      } else {
        if (clickedSessionIndex > 0) {
          treeManager.expandContinuationLevel(clickedLevel, clickedEvent, clickedSessionIndex);
        } else {
          treeManager.expandLevel(clickedLevel, clickedEvent);
        }
        onLevelsChange(treeManager.levels);
      }
    },
    [graphBuilder, levels, onLevelsChange, onContinuationRequest],
  );

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={nodeTypes}
      onNodeClick={onNodeClick}
      fitView
      fitViewOptions={{ padding: 0.15 }}
      proOptions={{ hideAttribution: true }}
      nodesDraggable={false}
      nodesConnectable={false}
      elementsSelectable={false}
      minZoom={0.1}
      maxZoom={2}
    >
      <Background color="#e2e8f0" gap={24} size={1} />
      <Controls showInteractive={false} />
    </ReactFlow>
  );
}

/* ─── Wrapper with Provider ───────────────────────────────────────────────── */

export function FlowVisualization(props: FlowVisualizationProps) {
  return (
    <ReactFlowProvider>
      <FlowVisualizationInner {...props} />
    </ReactFlowProvider>
  );
}
